import { createSlice, PayloadAction, createAction } from '@reduxjs/toolkit';
import {
  HosFormInfo,
  HosDailyInfo,
  TimeLine,
  UserLocationType,
  HosGridObject,
  PendingEditsType,
  PendingEditsListType,
  ApproveRejectPendingEditsType,
} from '../hosLog/types';
import {
  FetchLogsPayload,
  FetchLogPayload,
  ResponseListData,
  CertifyHosLogPayload,
  PendingException,
  SaveStatusPayload,
  SaveExceptionPayload,
  MultiplyCertifyHosLogs,
  ExportAllHosLogsPayload,
  SaveFormsPayload,
  FetchHosExportLinkPayload,
  FetchHosExportLinkResponse,
  EldMovement,
  FetchMovementsPayload,
} from 'src/common/types';
import { HosLog, HosLogObject } from '../types';
import { subDays } from 'date-fns';
import { hosRangeFilterDefault } from 'src/configs/gui-config';

interface HosFilterFormValues {
  violationsFilter: boolean;
  exceptionsFilter: boolean;
  pendingFilter: boolean;
  rejectedFilter: boolean;
  missingFilter: boolean;
  uncertifiedFilter: boolean;
}

export interface HosLogState {
  isEditingForms: boolean;
  isAddingException: boolean;
  isInsertingDutyStatus: boolean;
  timelineData: TimeLine;
  fcaDate: string;
  hosForms: HosFormInfo[];
  hosDaily: HosDailyInfo;
  hosLogs: Array<HosLog>;
  calendarHosLogs: Array<HosLog>;
  selectedHosLog: HosLogObject;
  currentDayHosLog: HosLogObject;
  selectedEditingStatus: HosGridObject | undefined;
  hosFilter: HosFilterFormValues;
  hosFilterFormState: HosFilterFormValues;
  hosRangeFilter: Array<Date>;
  userLocations: UserLocationType[];
  pendingExceptions: PendingException[];
  pendingRemovalExceptions: PendingException[];
  pendingEdits: PendingEditsType;
  activeViolationId: string;
  hosExportLink: string | null;
  hosExportJobId: string;
  hosLogEdits: {
    unsavedHOSLogEditsPresent: boolean;
    unsavedHOSExceptionsEditsPresent: boolean;
  };
}

const initialState: HosLogState = {
  isEditingForms: false,
  isAddingException: false,
  isInsertingDutyStatus: false,
  timelineData: {} as TimeLine,
  hosFilter: {
    violationsFilter: false,
    exceptionsFilter: false,
    pendingFilter: false,
    rejectedFilter: false,
    missingFilter: false,
    uncertifiedFilter: false,
  },
  hosFilterFormState: {
    violationsFilter: false,
    exceptionsFilter: false,
    pendingFilter: false,
    rejectedFilter: false,
    missingFilter: false,
    uncertifiedFilter: false,
  },
  hosRangeFilter: [subDays(new Date(), hosRangeFilterDefault), new Date()],
  fcaDate: 'Mon - Nov 22, 2020',
  hosForms: [
    { title: 'Tractor/Truck Numbers', data: '102535346' },
    { title: 'Trailer Numbers', data: '5325462' },
    { title: 'Shipping Numbers', data: '' },
    { title: 'Miles Driven', data: '' },
    { title: 'Co Driver', data: '' },
    { title: 'Additional Notes', data: '' },
  ],

  hosDaily: {
    date: {
      recordDate: '24 - Sep -21',
      startTime: '00:00',
      timeZone: 'EDT',
    },
    carrier: {
      carrier: '',
      udot: '',
    },
    driver: {
      name: '',
      driverId: '',
      licenseNumber: '',
      licenseState: '',
      coDriver: '',
    },
    tractor: [],
    eld: {
      provider: '',
      registrationId: '',
      identifier: '',
    },
    logDate: '',
    periodStartTime: '',
    timezone: '',
    exemptStatus: '',
    exemptReason: '',
  },
  hosLogs: [],
  calendarHosLogs: [],
  selectedHosLog: {} as HosLogObject,
  currentDayHosLog: {} as HosLogObject,
  selectedEditingStatus: undefined,
  pendingExceptions: [],
  pendingRemovalExceptions: [],
  userLocations: [],
  pendingEdits: {
    pendingEditsList: [],
    timelineData: {} as TimeLine,
  },
  activeViolationId: '',
  hosExportLink: '',
  hosExportJobId: '',
  hosLogEdits: {
    unsavedHOSLogEditsPresent: false,
    unsavedHOSExceptionsEditsPresent: false,
  },
};

interface SetLogDataPayload {
  fcaDate: string;
  hosDaily: HosDailyInfo;
}

export const reducerSlice = createSlice({
  name: 'hosLogReducer',
  initialState,
  reducers: {
    setIsEditingForms: (state, action: PayloadAction<boolean>): void => {
      state.isEditingForms = action.payload;
    },
    setIsAddingException: (state, action: PayloadAction<boolean>): void => {
      state.isAddingException = action.payload;
    },
    setInsertingDutyStatus: (state, action: PayloadAction<boolean>): void => {
      state.isInsertingDutyStatus = action.payload;
    },
    setLogData: (state, action: PayloadAction<SetLogDataPayload>): void => {
      state.hosDaily = action.payload.hosDaily;
      state.fcaDate = action.payload.fcaDate;
    },
    setHosLogs: (state, action: PayloadAction<ResponseListData>): void => {
      state.hosLogs = action.payload.results;
    },
    setHosExportJobId: (state, action: PayloadAction<FetchHosExportLinkPayload>): void => {
      state.hosExportJobId = action.payload.jobId;
    },
    setHosExportLink: (state, action: PayloadAction<FetchHosExportLinkResponse>): void => {
      state.hosExportLink = action.payload.reportUrl;
    },
    setHosLog: (state, action: PayloadAction<HosLogObject>): void => {
      state.selectedHosLog = action.payload;
    },
    setCurrentDayHosLog: (state, action: PayloadAction<HosLogObject>): void => {
      state.currentDayHosLog = action.payload;
    },
    setEditingStatus: (state, action: PayloadAction<HosGridObject | undefined>): void => {
      state.selectedEditingStatus = action.payload;
    },
    setFilter: (state, action): void => {
      state.hosFilter = action.payload;
    },
    resetFilter: (state): void => {
      state.hosFilter = initialState.hosFilter;
    },
    setFilterFormState: (state, action): void => {
      state.hosFilterFormState = action.payload;
    },
    resetFilterFormState: (state): void => {
      state.hosFilterFormState = initialState.hosFilterFormState;
    },
    setRangeFilter: (state, action): void => {
      state.hosRangeFilter = action.payload;
    },
    resetRangeFilter: (state): void => {
      state.hosRangeFilter = initialState.hosRangeFilter;
    },
    setTimelineData: (state, action: PayloadAction<TimeLine>): void => {
      state.timelineData = action.payload;
    },
    setUserLocationData: (state, action): void => {
      state.userLocations = action.payload;
    },
    addPendingException: (state, action: PayloadAction<PendingException>): void => {
      state.pendingExceptions = [...state.pendingExceptions, action.payload];
    },
    removePendingException: (state, action: PayloadAction<PendingException>): void => {
      const exception = action.payload;
      state.pendingExceptions = state.pendingExceptions.filter(item => {
        return item.exceptionTimestamp !== exception.exceptionTimestamp;
      });
    },
    removeException: (state, action: PayloadAction<PendingException>): void => {
      state.pendingRemovalExceptions = [...state.pendingRemovalExceptions, action.payload];
    },
    restoreException: (state, action: PayloadAction<PendingException>): void => {
      state.pendingRemovalExceptions = state.pendingRemovalExceptions.filter(item => {
        return item.exceptionId !== action.payload.exceptionId;
      });
    },
    clearEditState: (state): void => {
      state.pendingExceptions = [];
      state.pendingRemovalExceptions = [];
      state.isEditingForms = false;
      state.isInsertingDutyStatus = false;
      state.isAddingException = false;
      state.selectedEditingStatus = undefined;
    },
    setPendingEditsList: (state, action: PayloadAction<PendingEditsListType[]>): void => {
      state.pendingEdits.pendingEditsList = action.payload;
    },
    setPendingEditsTimelineData: (state, action: PayloadAction<TimeLine>): void => {
      state.pendingEdits.timelineData = action.payload;
    },
    resetPendingEditsTimelineData: (state): void => {
      state.pendingEdits.timelineData = initialState.pendingEdits.timelineData;
    },
    setActiveViolationId: (state, action: PayloadAction<string>): void => {
      state.activeViolationId = action.payload;
    },
    setCalendarHosLogs: (state, action: PayloadAction<ResponseListData>): void => {
      state.calendarHosLogs = action.payload.results;
    },
    setHosDaily: (state, action: PayloadAction<HosDailyInfo>): void => {
      state.hosDaily = {
        ...state.hosDaily,
        ...action.payload,
      };
    },
    resetHosDaily: (state): void => {
      state.selectedHosLog = initialState.selectedHosLog;
      state.currentDayHosLog = initialState.currentDayHosLog;
    },
    setUnsavedHOSLogEditsPresent: (state, action: PayloadAction<boolean>): void => {
      state.hosLogEdits.unsavedHOSLogEditsPresent = action.payload;
    },
    setUnsavedHOSExceptionsEditsPresent: (state, action: PayloadAction<boolean>): void => {
      state.hosLogEdits.unsavedHOSExceptionsEditsPresent = action.payload;
    },
  },
});

export const fetchHosLogs = createAction<FetchLogsPayload>('hosLogs/fetchLogs');
export const fetchHosLog = createAction<FetchLogPayload>('hosLog/fetchLog');
export const fetchCurrentDayHosLog = createAction<FetchLogPayload>('hosLog/fetchCurrentDayLog');
export const certifyHosLogAction = createAction<CertifyHosLogPayload>('hosLog/fetchCertifyLog');
export const multiplyCertifyHosLogsAction = createAction<MultiplyCertifyHosLogs>(
  'hosLog/fetchMultiplyCertifyLogs',
);
export const editLogStatusAction = createAction<SaveStatusPayload>('hosLog/fetchEditStatus');

export const saveExceptionLog = createAction<SaveExceptionPayload>('hosLog/saveExceptionLog');
export const fetchUserLocationsAction = createAction<string>('hosLog/fetchUserLocations');
export const insertDutyStatusAction = createAction<SaveStatusPayload>('hosLog/isertDutyStatus');

export const approveRejectPendingEditsAction = createAction<ApproveRejectPendingEditsType>(
  'pendingEdits/approveReject',
);
export const exportAllHosLogsAction = createAction<ExportAllHosLogsPayload>(
  'hosLog/exportAllHosLogs',
);
export const fetchHosExportLinkAction = createAction<FetchHosExportLinkPayload>(
  'hosLogs/fetchHosExportLink',
);

export const fetchCalendarHosLogsAction = createAction<FetchLogsPayload>(
  'hosLogs/fetchCalendarLogs',
);

export const saveLogFormsAction = createAction<SaveFormsPayload>('hosLog/saveLogForms');

export const {
  setIsEditingForms,
  setIsAddingException,
  setInsertingDutyStatus,
  setLogData,
  setHosLogs,
  setHosLog,
  setEditingStatus,
  setFilter,
  resetFilter,
  setFilterFormState,
  resetFilterFormState,
  setRangeFilter,
  resetRangeFilter,
  setTimelineData,
  setUserLocationData,
  addPendingException,
  removePendingException,
  removeException,
  restoreException,
  clearEditState,
  setPendingEditsList,
  setPendingEditsTimelineData,
  setCurrentDayHosLog,
  resetPendingEditsTimelineData,
  setActiveViolationId,
  setCalendarHosLogs,
  setHosDaily,
  resetHosDaily,
  setHosExportJobId,
  setHosExportLink,
  setUnsavedHOSLogEditsPresent,
  setUnsavedHOSExceptionsEditsPresent,
} = reducerSlice.actions;

export default reducerSlice.reducer;
